import React, { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import API from "../services/api"
import BottomNav from "../components/BottomNav"
import { getUser } from '../utils/storage'
import { useToast } from "../components/GlobalToast"
import BannerSlider from "../components/BannerSlider"


const Wallet = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const [balance, setBalance] = useState("0")
  const [holdBalance, setHoldBalance] = useState("0")
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddMoney, setShowAddMoney] = useState(false)
  const [step, setStep] = useState(1) // 1: Amount, 2: Pay, 3: UTR
  const [amount, setAmount] = useState("")
  const [utr, setUtr] = useState("")
  const [paymentMethod, setPaymentMethod] = useState("")

  const user = getUser()

  useEffect(() => {
    fetchWalletData()

    // ✅ If coming from Share funnel, auto-open Add Cash modal
    if (location.state?.fromShare) {
        setShowAddMoney(true);
    }
  }, [])

  const fetchWalletData = async () => {
    try {
      const balanceRes = await API.get(`/wallet/balance?userId=${user.id || 1}`)
      setBalance(balanceRes.data.balance)
      setHoldBalance(balanceRes.data.hold_balance || "0")

      const txRes = await API.get(`/wallet/transactions?userId=${user.id || 1}`)
      setTransactions(txRes.data.transactions)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  /* 
  const loadRazorpay = () => { ... }
  */

  const handleDepositRequest = async () => {
    if (!utr || utr.length < 8) return toast.warn("Please enter a valid 12-digit UTR/Transaction ID")
    
    setLoading(true)
    try {
      const res = await API.post("/wallet/deposit-request", {
        userId: user.id,
        amount: parseFloat(amount),
        utr: utr,
        method: paymentMethod
      })

      if (res.data.success) {
        toast.success("Request Submitted! 🎉 ₹" + amount + " is now on hold until admin approval.")
        setShowAddMoney(false)
        setStep(1)
        setAmount("")
        setUtr("")
        fetchWalletData()
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to submit request")
    } finally {
      setLoading(false)
    }
  }

  const openUPIApp = (app) => {
    setPaymentMethod(app)
    const upiId = "prasthcreation@ptyes" 
    const name = "Winrox11"
    const upiLink = `upi://pay?pa=${upiId}&pn=${name}&am=${amount}&cu=INR`
    
    // Deep link to open UPI apps
    window.location.href = upiLink
    
    // Move to UTR verification step
    setTimeout(() => setStep(3), 1000)
  }

  return (
    <div className="bg-theme-bg min-h-screen text-theme-text pb-24 font-sans">
      
      {/* 🔝 HEADER */}
      <div className="p-4 bg-theme-card border-b border-theme-border flex items-center sticky top-0 z-50">
        <button
          onClick={() => navigate("/")}
          className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border text-theme-text transition-all active:scale-95 shadow-theme-sm"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2 className="font-bold text-lg text-theme-text tracking-tight uppercase flex-1 text-center">My Wallet</h2>
        <div className="w-10 h-10"></div>
      </div>

      <div className="p-5 space-y-8">
        
        {/* 💳 BALANCE CARD (Minimal Redesign) */}
        <div className="glass-card rounded-theme p-8 shadow-theme-lg relative border border-theme-border/50 overflow-hidden animate-float-subtle mt-4">
            <div className="flex justify-between items-start mb-4">
               <span className="text-[11px] text-theme-text-sec font-extrabold uppercase tracking-[0.2em]">Available Balance</span>
               {Number(holdBalance) > 0 && (
                 <div className="flex items-center gap-2 bg-yellow-500/10 px-3 py-1 rounded-full border border-yellow-500/20 shadow-inner">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(234,179,8,0.5)]"></span>
                    <span className="text-[9px] font-black text-yellow-600 uppercase tracking-widest">Verification Pending</span>
                 </div>
               )}
            </div>
            
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-accent">₹</span>
              <h1 className="text-5xl font-black text-theme-text tracking-tight animate-fade-in">{balance}</h1>
            </div>
            
            {Number(holdBalance) > 0 && (
                <div className="mt-5 bg-theme-bg/30 rounded-2xl p-3 border border-theme-border backdrop-blur-sm">
                    <p className="text-[10px] text-theme-text-sec font-bold uppercase tracking-wider">Processing: <span className="text-yellow-600 font-black ml-1">₹{holdBalance}</span></p>
                </div>
            )}
            
            <div className="mt-10 flex gap-4">
                <button 
                  onClick={() => { setStep(1); setShowAddMoney(true); }}
                  className="flex-1 bg-accent text-white px-6 py-4 rounded-btn font-black text-sm shadow-md active:scale-95 transition-all hover:brightness-105 hover-glow uppercase tracking-widest"
                >
                    + Add Cash
                </button>
                <button 
                  onClick={() => navigate("/withdrawal")}
                  className="flex-1 bg-theme-bg/50 border border-theme-border text-theme-text px-6 py-4 rounded-btn font-bold text-sm active:scale-95 transition-all hover:bg-theme-bg-sec backdrop-blur-sm uppercase tracking-widest"
                >
                    Withdraw
                </button>
            </div>
        </div>

        {/* 📢 AD BANNERS */}
        <div className="mt-4">
          <BannerSlider />
        </div>

        {/* 📈 TRANSACTION HISTORY */}
        <div className="space-y-4">
            <div className="flex justify-between items-center px-1">
                <h3 className="text-base font-bold text-theme-text uppercase tracking-tight">Recent Transactions</h3>
            </div>

            {loading ? (
                <div className="flex justify-center p-12">
                    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : transactions.length === 0 ? (
                <div className="bg-theme-card rounded-theme p-12 text-center border border-theme-border shadow-theme-sm">
                    <p className="text-5xl mb-4 opacity-30">💸</p>
                    <p className="text-theme-text-sec font-bold text-[10px] uppercase tracking-widest">No activities yet</p>
                </div>
            ) : (
                <div className="bg-theme-card rounded-theme border border-theme-border overflow-hidden shadow-theme-sm">
                    {transactions.map((tx, idx) => (
                        <div key={tx.id} className={`p-5 flex justify-between items-center ${idx !== transactions.length -1 ? 'border-b border-theme-border' : ''}`}>
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm shrink-0 font-bold border ${tx.type === 'CREDIT' ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`}>
                                    {tx.type === 'CREDIT' ? 'IN' : 'OUT'}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-theme-text text-sm truncate">{tx.description || (tx.type === 'CREDIT' ? 'Deposit' : 'Entry Fee')}</p>
                                    <p className="text-[10px] text-theme-text-sec font-medium uppercase mt-0.5 tracking-tight">{new Date(tx.created_at).toLocaleDateString()} • {new Date(tx.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                                </div>
                            </div>
                            <p className={`font-bold text-base ${tx.type === 'CREDIT' ? 'text-green-600' : 'text-red-500'}`}>
                                {tx.type === 'CREDIT' ? '+' : '-'}₹{Math.abs(tx.amount)}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>

      </div>

      {/* 💸 ADD MONEY MODAL */}
      {showAddMoney && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-theme-bg/80 backdrop-blur-sm p-4 animate-fade-in">
            <div className="glass-card w-full max-w-sm rounded-theme p-8 border border-theme-border shadow-theme-lg animate-scale-up relative overflow-hidden">
                <div className="flex justify-between items-center mb-8 relative z-10">
                    <h3 className="text-xl font-black text-theme-text uppercase tracking-widest italic">
                        {step === 1 ? "Deposit Cash" : step === 2 ? "Select App" : "Verify Payment"}
                    </h3>
                    <button onClick={() => setShowAddMoney(false)} className="text-theme-text-sec hover:text-theme-text p-2.5 rounded-full bg-theme-bg/50 active:scale-90 transition-all border border-theme-border">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                </div>
                
                {step === 1 && (
                    <div className="space-y-6 relative z-10">
                        <div className="relative">
                            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-2xl font-bold text-accent">₹</span>
                            <input 
                              type="number"
                              value={amount}
                              onChange={(e) => setAmount(e.target.value)}
                              className="w-full bg-theme-bg border border-theme-border focus:border-accent rounded-xl py-5 pl-12 pr-6 text-2xl font-bold outline-none transition-all placeholder:text-theme-text-sec/30"
                              placeholder="0"
                              autoFocus
                            />
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3">
                            {[100, 200, 500].map(val => (
                                <button 
                                  key={val}
                                  type="button" 
                                  onClick={() => setAmount((prev) => (Number(prev || 0) + val).toString())}
                                  className="bg-theme-bg border border-theme-border hover:bg-theme-bg-sec py-2.5 rounded-lg text-[10px] font-bold text-theme-text transition-all active:scale-95"
                                >
                                    +₹{val}
                                </button>
                            ))}
                        </div>

                        <button 
                          onClick={() => {
                            if (!amount || amount < 100) return toast.warn("Min deposit ₹100");
                            setStep(2);
                          }}
                          className="w-full bg-accent text-white py-4 rounded-xl font-bold tracking-wider uppercase shadow-sm transition-all hover:brightness-105 active:scale-95 mt-4"
                        >
                            Proceed to Payment →
                        </button>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-4 relative z-10">
                        <p className="text-[10px] text-theme-text-sec font-bold text-center uppercase tracking-widest mb-4">Choose an app to pay ₹{amount}</p>
                        
                        <div className="grid grid-cols-1 gap-3">
                            <button onClick={() => openUPIApp('GPay')} className="flex items-center justify-between bg-theme-bg border border-theme-border p-4 rounded-xl active:scale-[0.98] transition-all hover:border-accent/40">
                                <span className="font-bold text-sm">Google Pay</span>
                                <span className="text-xl">📱</span>
                            </button>
                            <button onClick={() => openUPIApp('PhonePe')} className="flex items-center justify-between bg-theme-bg border border-theme-border p-4 rounded-xl active:scale-[0.98] transition-all hover:border-accent/40">
                                <span className="font-bold text-sm">PhonePe</span>
                                <span className="text-xl">🟣</span>
                            </button>
                            <button onClick={() => openUPIApp('Paytm')} className="flex items-center justify-between bg-theme-bg border border-theme-border p-4 rounded-xl active:scale-[0.98] transition-all hover:border-accent/40">
                                <span className="font-bold text-sm">Paytm</span>
                                <span className="text-xl">🔵</span>
                            </button>
                        </div>


                        <button onClick={() => setStep(3)} className="w-full py-3 text-theme-text-sec text-[10px] font-bold uppercase tracking-wider">I have completed the payment →</button>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-6 relative z-10 text-center">
                        <p className="text-[11px] text-theme-text font-bold uppercase mb-2 tracking-wide">Enter 12-Digit Transaction ID (UTR)</p>
                        
                        <input 
                          type="text"
                          value={utr}
                          onChange={(e) => setUtr(e.target.value)}
                          className="w-full bg-theme-bg border border-theme-border focus:border-accent rounded-xl py-5 px-6 text-xl font-bold text-center outline-none transition-all placeholder:text-theme-text-sec/30 shadow-inner"
                          placeholder="0000 0000 0000"
                        />

                        <div className="bg-green-500/5 border border-green-500/10 p-5 rounded-2xl text-left">
                            <p className="text-[10px] text-theme-text font-bold uppercase leading-relaxed tracking-wide text-center">
                                ✅ Deposit will be credited to your wallet within <span className="text-green-600">5 minutes</span> after verification.
                            </p>
                            <p className="text-[9px] text-theme-text-sec font-medium mt-2 text-center uppercase">
                                Please ensure the UTR number and amount are correct to avoid delays.
                            </p>
                        </div>

                        <button 
                          onClick={handleDepositRequest}
                          disabled={loading}
                          className="w-full bg-accent text-white py-4 rounded-xl font-bold tracking-wider uppercase shadow-md transition-all hover:brightness-105 active:scale-95"
                        >
                            {loading ? "Verifying..." : "Confirm Deposit"}
                        </button>

                        <button onClick={() => setStep(2)} className="text-theme-text-sec text-[10px] font-bold uppercase py-2 hover:text-theme-text">← Back to Payment</button>
                    </div>
                )}
            </div>
        </div>
      )}

      <BottomNav activeTab="profile" />
    </div>
  )
}

export default Wallet
